import { useState, useEffect, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Papa from 'papaparse';

import styled from '@emotion/styled/macro';
import { Button, Box, Text, Alert, AlertIcon, Flex } from '@chakra-ui/react';

import MainModal from './MainModal';
import DataGrid from '../grid/DataGrid';
import DataGridWrapper from '../grid/DataGridWrapper';

import TextareaField from '../forms/TextareaField';

import { removeTrailingNewLine } from '../../services/items';

import useDebounce from '../../hooks/useDebounce';

const AddRowsModal = ({ columns, addRows, tabIndex, validate, format, columnSchema, selectTypes = null, ...rest }) => {
    const intl = useIntl();

    const [rawInput, setRawInput] = useState('');
    const [parsedData, setParsedData] = useState([]);
    const [error, setError] = useState({ disabled: false, message: '' });

    const debouncedInput = useDebounce(rawInput, 250);

    const hasDate = useMemo(() => {
        return Object.values(columnSchema).some((e) => e.type === 'date');
    }, [columnSchema]);

    const hasError = useCallback(
        (data) => {
            const errorMsg = intl.formatMessage({ id: 'common_add_rows_error_message' });

            const validRows = validate(data, columnSchema, tabIndex).flat();

            const hasError = validRows.find((cell) => cell === true);

            if (hasError) {
                setError({ disabled: true, message: errorMsg });
            } else {
                setError({ disabled: false, message: '' });
            }
        },
        [columnSchema, intl, tabIndex, validate]
    );

    useEffect(() => {
        const header = columns
            .map((col) => col.field)
            .join('\t')
            .concat('\n');

        Papa.parse(header + debouncedInput, {
            header: true,
            skipEmptyLines: 'greedy', // Greedy remove empty lines
            worker: true,

            complete(results) {
                // Papa parse leaves \r on random at the end of some fields e.g.(...T14:48:00.000Z\r)
                const sanitizedData = removeTrailingNewLine(results.data);

                // check for errors and set message
                hasError(sanitizedData);

                setParsedData(sanitizedData);
            },
        });
    }, [columns, debouncedInput, hasError]);

    const clearRows = () => {
        setError({ disabled: false, message: '' });
        setRawInput('');
        setParsedData([]);
    };

    const onPasteData = (event) => {
        const data = event.clipboardData.getData('text');
        setRawInput(data);
    };

    const onEditData = (event) => {
        setRawInput(event.target.value);

        if (event.target.value === '') clearRows();
    };

    const onAddRows = () => {
        const data = format(parsedData, selectTypes, tabIndex);

        addRows(data);

        clearRows();
    };

    return (
        <MainModal
            isOpen
            onClose={() => {
                rest.onClose();
                setParsedData([]);
            }}
            header={<FormattedMessage id="common_add_rows_heading" />}
            content={
                <>
                    <Text variant="regular" mb={2}>
                        <FormattedMessage id="common_add_rows_sub_heading" />
                    </Text>

                    <Flex>
                        <TextareaField
                            isInvalid={error.disabled}
                            onPaste={onPasteData}
                            onChange={onEditData}
                            placeholder={intl.formatMessage({ id: 'common_add_rows_input_placeholder' })}
                            error={error.message}
                        />

                        {hasDate && (
                            <CustomAlert>
                                <AlertIcon />
                                <Box>
                                    <Text fontWeight="700">
                                        <FormattedMessage id="common_date_format_requirements" />
                                    </Text>
                                    <Text>
                                        <FormattedMessage id="common_date_format" />
                                    </Text>
                                </Box>
                            </CustomAlert>
                        )}
                    </Flex>

                    <Box my={4}>
                        <DataGridWrapper>
                            <DataGrid
                                rowData={parsedData}
                                columns={columns.map((column) => ({ ...column, editable: false }))}
                                columnSchema={columnSchema}
                                isInitialRequestSent={true}
                                rowModelType="clientSide"
                            />
                        </DataGridWrapper>
                    </Box>
                </>
            }
            footerRightSlot={
                <Button
                    isLoading={rest.isLoading}
                    isDisabled={parsedData.length === 0 || error.disabled}
                    ml={4}
                    onClick={onAddRows}
                    textTransform="capitalize"
                >
                    <FormattedMessage id="common_add" />
                </Button>
            }
        />
    );
};

const CustomAlert = styled(Alert)`
    align-items: flex-start;
    border: 1px solid var(--chakra-colors-blue-500);
    border-radius: 6px;
    background: var(--chakra-colors-blue-50);
    font-size: 11px;
    margin-left: 15px;
    margin-top: 3px;
    padding: 10px;
    height: 80%;
`;

export default AddRowsModal;
